<template>
  <div class="order" style="background: #F7F5F2; min-height: 100vh;">
    <div class="personal-center">
      <div class="user-info">
        <div v-if="isLoggedIn" class="user-detail">
          <!-- 左侧：头像和用户信息 -->
          <div class="user-left">
            <div class="avatar-wrapper">
              <div class="avatar-container">
                <img v-if="contactInfo.avatar" :src="$common.getFullImageUrl(contactInfo.avatar, $config, 'm')"
                  class="avatar-img" @click="previewAvatar" />
                <div v-else class="avatar-placeholder" @click="previewAvatar">
                  {{ (contactInfo.userName || userMobile || $t('anonymous'))[0] }}
                </div>
                <div v-if="uploadingAvatar" class="avatar-uploading">
                  <van-loading type="spinner" size="20" color="#fff" />
                </div>
                <div v-if="!isOtherProfile" class="avatar-edit-icon" @click="triggerAvatarUpload">
                  <van-icon name="photograph" size="12" />
                </div>
              </div>

              <input type="file" ref="avatarInput" accept="image/*" style="display: none" @change="handleAvatarChange" />
            </div>
            <div v-if="userMobile" class="mobile-display">{{ userMobile }}</div>

            <!-- Name display/edit section -->
            <div class="name-section">
              <div v-if="!isEditingName" class="name-display">
                <span class="name-label">{{ $t('label_name') }}</span>
                <span class="name-value">{{ contactInfo.userName || '-' }}</span>
                <van-icon v-if="!isOtherProfile" name="edit" class="edit-icon" @click="startEditName" />
              </div>
              <div v-else class="name-edit">
                <van-field v-model="contactInfo.userName" :placeholder="$t('placeholder_enter_name')" class="name-input" />
                <van-button plain type="primary" size="mini" :loading="saving" @click="handleSaveName">{{ $t('save') }}</van-button>
                <van-button plain size="mini" @click="cancelEditName">{{ $t('cancel') }}</van-button>
              </div>
            </div>

            <!-- Bio display/edit section -->
            <div class="bio-section">
              <div v-if="!isEditingBio" class="bio-display">
                <span class="name-label">{{ $t('label_bio') }}</span>
                <span v-if="contactInfo.bio" class="bio-value">{{ contactInfo.bio }}</span>
                <span v-else-if="!isOtherProfile" class="bio-placeholder">{{ $t('placeholder_bio_empty') }}</span>
                <van-icon v-if="!isOtherProfile" name="edit" class="edit-icon" @click="startEditBio" />
              </div>
              <div v-else class="bio-edit">
                <van-field v-model="contactInfo.bio" type="textarea" rows="2" :placeholder="$t('placeholder_enter_bio')" class="bio-input" maxlength="256" show-word-limit />
                <div class="bio-edit-actions">
                  <van-button plain type="primary" size="mini" :loading="savingBio" @click="handleSaveBio">{{ $t('save') }}</van-button>
                  <van-button plain size="mini" @click="cancelEditBio">{{ $t('cancel') }}</van-button>
                </div>
              </div>
            </div>

            <!-- Joined date + stats row -->
            <div v-if="joinedText" class="joined-text">{{ $t('profile_joined', { date: joinedText }) }}</div>
            <div class="profile-stats-row">
              <span class="profile-stats-item" @click="switchTab(0)"><span class="profile-stats-num">{{ statsPosts }}</span><span class="profile-stats-label">{{ $t('profile_stats_posts') }}</span></span>
              <span class="profile-stats-dot">·</span>
              <span v-if="!isOtherProfile" class="profile-stats-item" @click="switchTab(1)"><span class="profile-stats-num">{{ statsComments }}</span><span class="profile-stats-label">{{ $t('profile_stats_comments') }}</span></span>
              <span v-if="!isOtherProfile" class="profile-stats-dot">·</span>
              <span class="profile-stats-item"><span class="profile-stats-num">{{ statsLikesReceived }}</span><span class="profile-stats-label">{{ $t('profile_stats_likes') }}</span></span>
            </div>

            <div class="header-actions-row">
              <!-- 他人 profile：返回 + 「…」菜单（拉黑/取消拉黑） -->
              <template v-if="isOtherProfile">
                <div class="settings-trigger" @click="handleBack">
                  <van-icon name="arrow-left" size="18" />
                </div>
                <div class="settings-trigger" @click="showUserMenu = true">
                  <van-icon name="ellipsis" size="18" />
                </div>
              </template>
              <div v-else class="settings-trigger" @click="goToSettings">
                <van-icon name="setting-o" size="18" />
                <span class="settings-label">{{ $t('settings') }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="login-prompt">
          <div class="login-prompt-inner">
            <van-button type="primary" size="small" class="btn" @click="handleLogin">{{ $t('login') }}</van-button>
            <div class="login-prompt-slogan">{{ $t('slogan_ai') }}</div>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div v-if="isLoggedIn" class="profile-tabs-section">
        <div class="profile-tab-buttons">
          <div v-for="(tab, idx) in tabs" :key="idx" class="profile-tab-pill"
            :class="{ active: selectedTab === idx }" @click="switchTab(idx)">
            <van-icon :name="tab.icon" size="12" />
            <span>{{ tab.label }}</span>
          </div>
          <div v-if="!isOtherProfile" class="profile-tab-search" @click="openProfileSearch">
            <van-icon name="search" size="18" />
          </div>
        </div>

        <div class="profile-tab-content">
          <!-- Messages -->
          <div v-if="selectedTab === 0" class="tab-panel">
            <div v-if="mySessionsLoading && !mySessionsLoaded" class="tab-loading">
              <van-loading size="24" />
            </div>
            <div v-else-if="mySessionsLoaded && mySessions.length === 0" class="tab-empty">
              <van-icon name="chat-o" size="48" color="#E0DDD8" />
              <p>{{ $t('profile_empty_messages') }}</p>
            </div>
            <div v-else>
              <div v-for="session in mySessions" :key="session.chatSessionId" class="profile-session-card profile-session-card-mine"
                @click="navigateToSession(session.chatSessionId)">
                <!-- 卡片右上角快捷设置（持续匹配/公开/允许直接联系） -->
                <van-icon v-if="!isOtherProfile" name="setting-o" size="14" color="#8A8A8A" class="profile-session-quick-settings"
                  @click.stop="openQuickSettings(session)" />
                <div v-if="session.channelName" class="profile-channel-tag">
                  <van-icon name="label-o" size="10" />
                  <span>{{ session.channelName }}</span>
                </div>
                <div v-if="session.userMsg" class="profile-session-usermsg">{{ $common.formatUserMsgPreview(session.userMsg) }}</div>
                <div v-if="session.aiSummary" :class="['profile-session-ai', session.userMsg ? 'has-user-msg' : '']">
                  <span class="profile-session-ai-tag">{{ $t('aiChat_matchSummaryLabel') }}</span>
                  <span class="profile-session-ai-text">{{ session.aiSummary }}</span>
                </div>
                <div class="profile-session-footer">
                  <div class="profile-session-stats">
                    <span class="profile-stat">
                      <van-icon name="comment-o" size="12" />
                      {{ session.chatSessionCommentCnt }}
                    </span>
                    <span class="profile-stat">
                      <van-icon name="like-o" size="12" />
                      {{ session.likeCnt }}
                    </span>
                  </div>
                  <span v-if="session.createdAt" class="profile-session-time">{{ $common.formatDateTime(session.createdAt) }}</span>
                </div>
              </div>
              <div v-if="mySessionsHasMore" class="load-more-row">
                <van-button v-if="!mySessionsLoading" size="small" plain hairline type="primary"
                  @click="loadMoreMySessions">{{ $t('channel_chat_session_loadMore') }}</van-button>
                <van-loading v-else size="20" />
              </div>
            </div>
          </div>

          <!-- Comments -->
          <div v-if="selectedTab === 1" class="tab-panel">
            <div v-if="myCommentsLoading && !myCommentsLoaded" class="tab-loading">
              <van-loading size="24" />
            </div>
            <div v-else-if="myCommentsLoaded && myComments.length === 0" class="tab-empty">
              <van-icon name="comment-o" size="48" color="#E0DDD8" />
              <p>{{ $t('profile_empty_comments') }}</p>
            </div>
            <div v-else>
              <div v-for="item in myComments" :key="item.comment.commentId" class="profile-comment-card"
                @click="navigateToSession(item.comment.chatSessionId)">
                <div v-if="item.chatSession && item.chatSession.channelName" class="profile-channel-tag">
                  <van-icon name="label-o" size="10" />
                  <span>{{ item.chatSession.channelName }}</span>
                </div>
                <div class="profile-comment-bubble">
                  <img v-if="item.comment.userAvatar" :src="$common.getFullImageUrl(item.comment.userAvatar, $config, 's')"
                    class="profile-comment-avatar">
                  <div v-else class="profile-comment-avatar-placeholder">
                    {{ (item.comment.userName || 'U')[0] }}
                  </div>
                  <div class="profile-comment-body">
                    <div v-if="item.comment.replyToUserName" class="profile-comment-reply">
                      {{ $t('profile_reply_to', { name: item.comment.replyToUserName }) }}
                    </div>
                    <div class="profile-comment-text">{{ item.comment.content }}</div>
                  </div>
                </div>
                <div v-if="item.chatSession" class="profile-comment-preview">
                  <van-icon name="chat-o" size="12" />
                  <span>{{ $common.formatUserMsgPreview(item.chatSession.userMsg) || item.chatSession.aiSummary || '' }}</span>
                </div>
                <div v-if="item.comment.createdAt" class="profile-comment-time">{{ $common.formatSmartTime(item.comment.createdAt, $i18n.locale) }}</div>
              </div>
              <div v-if="myCommentsHasMore" class="load-more-row">
                <van-button v-if="!myCommentsLoading" size="small" plain hairline type="primary"
                  @click="loadMoreMyComments">{{ $t('channel_chat_session_loadMore') }}</van-button>
                <van-loading v-else size="20" />
              </div>
            </div>
          </div>

          <!-- Likes -->
          <div v-if="selectedTab === 2" class="tab-panel">
            <div v-if="myLikesLoading && !myLikesLoaded" class="tab-loading">
              <van-loading size="24" />
            </div>
            <div v-else-if="myLikesLoaded && myLikes.length === 0" class="tab-empty">
              <van-icon name="like-o" size="48" color="#E0DDD8" />
              <p>{{ $t('profile_empty_likes') }}</p>
            </div>
            <div v-else>
              <div v-for="session in myLikes" :key="session.chatSessionId" class="profile-session-card"
                @click="navigateToSession(session.chatSessionId)">
                <div v-if="session.channelName" class="profile-channel-tag">
                  <van-icon name="label-o" size="10" />
                  <span>{{ session.channelName }}</span>
                </div>
                <div v-if="session.userMsg" class="profile-session-usermsg">{{ $common.formatUserMsgPreview(session.userMsg) }}</div>
                <div v-if="session.aiSummary" :class="['profile-session-ai', session.userMsg ? 'has-user-msg' : '']">
                  <span class="profile-session-ai-tag">{{ $t('aiChat_matchSummaryLabel') }}</span>
                  <span class="profile-session-ai-text">{{ session.aiSummary }}</span>
                </div>
                <div class="profile-session-footer">
                  <div class="profile-session-stats">
                    <span class="profile-stat liked">
                      <van-icon name="like" size="12" />
                      {{ $t('chatSessionLike_liked') }}
                    </span>
                    <span class="profile-stat">
                      <van-icon name="comment-o" size="12" />
                      {{ session.chatSessionCommentCnt }}
                    </span>
                    <span class="profile-stat">
                      <van-icon name="like-o" size="12" />
                      {{ session.likeCnt }}
                    </span>
                  </div>
                  <span v-if="session.createdAt" class="profile-session-time">{{ $common.formatDateTime(session.createdAt) }}</span>
                </div>
              </div>
              <div v-if="myLikesHasMore" class="load-more-row">
                <van-button v-if="!myLikesLoading" size="small" plain hairline type="primary"
                  @click="loadMoreMyLikes">{{ $t('channel_chat_session_loadMore') }}</van-button>
                <van-loading v-else size="20" />
              </div>
            </div>
          </div>

          <!-- Bookmarks -->
          <div v-if="selectedTab === 3" class="tab-panel">
            <div v-if="myBookmarksLoading && !myBookmarksLoaded" class="tab-loading">
              <van-loading size="24" />
            </div>
            <div v-else-if="myBookmarksLoaded && myBookmarks.length === 0" class="tab-empty">
              <van-icon name="star-o" size="48" color="#E0DDD8" />
              <p>{{ $t('profile_empty_bookmarks') }}</p>
            </div>
            <div v-else>
              <div v-for="session in myBookmarks" :key="session.chatSessionId" class="profile-session-card"
                @click="navigateToSession(session.chatSessionId)">
                <div v-if="session.channelName" class="profile-channel-tag">
                  <van-icon name="label-o" size="10" />
                  <span>{{ session.channelName }}</span>
                </div>
                <div v-if="session.userMsg" class="profile-session-usermsg">{{ $common.formatUserMsgPreview(session.userMsg) }}</div>
                <div v-if="session.aiSummary" :class="['profile-session-ai', session.userMsg ? 'has-user-msg' : '']">
                  <span class="profile-session-ai-tag">{{ $t('aiChat_matchSummaryLabel') }}</span>
                  <span class="profile-session-ai-text">{{ session.aiSummary }}</span>
                </div>
                <div class="profile-session-footer">
                  <div class="profile-session-stats">
                    <span class="profile-stat bookmarked">
                      <van-icon name="star" size="12" />
                      {{ $t('chatSessionBookmark_bookmarked') }}
                    </span>
                    <span class="profile-stat">
                      <van-icon name="comment-o" size="12" />
                      {{ session.chatSessionCommentCnt }}
                    </span>
                    <span class="profile-stat">
                      <van-icon name="like-o" size="12" />
                      {{ session.likeCnt }}
                    </span>
                  </div>
                  <span v-if="session.createdAt" class="profile-session-time">{{ $common.formatDateTime(session.createdAt) }}</span>
                </div>
              </div>
              <div v-if="myBookmarksHasMore" class="load-more-row">
                <van-button v-if="!myBookmarksLoading" size="small" plain hairline type="primary"
                  @click="loadMoreMyBookmarks">{{ $t('channel_chat_session_loadMore') }}</van-button>
                <van-loading v-else size="20" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 搜索弹窗 -->
    <van-popup v-model="showProfileSearch" round position="bottom" :style="{ height: '85%' }" :lazy-render="false">
      <div class="profile-search-popup">
        <div class="profile-search-header">
          <div class="profile-search-input-wrap">
            <van-icon name="search" size="14" color="#999" />
            <input
              v-model="profileSearchQuery"
              type="text"
              style="flex: 1; border: none; background: transparent; padding: 8px; font-size: 14px; outline: none;"
              @input="onProfileSearchInput"
            />
            <van-icon v-if="profileSearchQuery" name="clear" size="14" color="#999" style="cursor: pointer;"
              @click="clearProfileSearch" />
          </div>
          <span style="color: #8A8A8A; font-size: 14px; cursor: pointer; margin-left: 12px;" @click="showProfileSearch = false">
            {{ $t('cancel') }}
          </span>
        </div>

        <!-- 搜索 tab -->
        <div class="profile-search-tabs">
          <div v-for="(tab, idx) in profileSearchTabs" :key="idx"
            class="profile-search-tab" :class="{ active: profileSearchTab === idx }" @click="profileSearchTab = idx">
            {{ tab.label }}
          </div>
        </div>

        <!-- 搜索结果列表 -->
        <div style="flex: 1; overflow-y: auto; padding: 0 16px;">
          <div v-if="profileSearchLoading" style="display: flex; justify-content: center; padding: 40px;">
            <van-loading size="24" />
          </div>

          <div v-else-if="profileSearchResults.length === 0 && profileSearchQuery.trim() && profileSearchHasSearched"
            style="text-align: center; padding: 40px; color: #8A8A8A;">
            <van-icon name="search" size="48" color="#ddd" />
            <div style="margin-top: 16px;">{{ $t('channels_noResults') }}</div>
          </div>

          <div v-else-if="profileSearchResults.length === 0 && !profileSearchQuery.trim()" style="text-align: center; padding: 40px; color: #8A8A8A;">
            <van-icon name="search" size="48" color="#ddd" />
            <div style="margin-top: 16px;">{{ $t('search') }}</div>
          </div>

          <!-- Messages / Likes / Bookmarks 结果 -->
          <div v-if="profileSearchTab !== 1">
            <div v-for="session in profileSearchResults" :key="session.chatSessionId" class="profile-search-result-card"
              @click="navigateToSession(session.chatSessionId)">
              <div v-if="session.channelName" class="profile-channel-tag">
                <van-icon name="label-o" size="10" />
                <span>{{ session.channelName }}</span>
              </div>
              <div v-if="session.userMsg" class="profile-search-result-title">{{ $common.formatUserMsgPreview(session.userMsg) }}</div>
              <div v-if="session.aiSummary" class="profile-search-result-summary">{{ session.aiSummary }}</div>
            </div>
          </div>

          <!-- Comments 结果 -->
          <div v-else>
            <div v-for="item in profileSearchResults" :key="item.comment.commentId" class="profile-search-result-card"
              @click="navigateToSession(item.comment.chatSessionId)">
              <div class="profile-search-comment-content">{{ item.comment.content }}</div>
              <div v-if="item.chatSession" class="profile-search-comment-session">
                {{ $common.formatUserMsgPreview(item.chatSession.userMsg || '') || item.chatSession.aiSummary }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 头像裁剪对话框 -->
    <avatar-crop-dialog ref="cropDialog" :image-file="selectedImageFile" @confirm="handleCropConfirm" />

    <!-- 他人 profile「…」菜单弹窗（拉黑/取消拉黑） -->
    <ContactMethods :visible.sync="showUserMenu"
      :contactInfo="contactInfo"
      :targetUserId="otherUserId"
      :userName="contactInfo.userName"
      :userAvatar="contactInfo.avatar"
      :userBio="contactInfo.bio" />

    <!-- 需求卡快捷设置（卡片右上角齿轮直达：持续匹配/公开/允许直接联系） -->
    <van-action-sheet v-model="showQuickSettings" :actions="quickSettingActions"
      :cancel-text="$t('message_cancel')" close-on-click-action
      @select="onQuickSettingSelect" @cancel="showQuickSettings = false" />
  </div>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import { Col, Row, Button, Popup, Toast, Form, Field, CellGroup, Icon, Loading, Dialog, ImagePreview, ActionSheet } from 'vant'
import AvatarCropDialog from './AvatarCropDialog.vue'
import ContactMethods from './ContactMethods.vue'
Vue.use(Col)
Vue.use(Row)
Vue.use(Button)
Vue.use(Popup)
Vue.use(Toast)
Vue.use(Form)
Vue.use(Field)
Vue.use(CellGroup)
Vue.use(Icon)
Vue.use(Loading)
Vue.use(Dialog)
Vue.use(ImagePreview)
Vue.use(ActionSheet)

export default {
  name: 'PersonalCenter',
  components: {
    AvatarCropDialog,
    ContactMethods
  },
  props: {
    msg: String
  },
  data () {
    return {
      value: '',
      userMobile: '',
      // 他人只读 profile：对方 userId（data 初始化时即读取，保证 loadMySessions 时序正确）与「…」菜单显隐
      otherUserId: String(this.$route.query.userId || ''),
      showUserMenu: false,
      contactInfo: {
        email: '',
        userName: '',
        avatar: '',
        bio: ''
      },
      saving: false,
      uploadingAvatar: false,
      selectedImageFile: null,
      isEditingName: false,
      isEditingBio: false,
      originalName: '',
      originalBio: '',
      savingBio: false,
      selectedTab: 0,
      mySessions: [],
      mySessionsLoading: false,
      mySessionsLoaded: false,
      mySessionsHasMore: true,
      mySessionsCurPage: 1,
      myComments: [],
      myCommentsLoading: false,
      myCommentsLoaded: false,
      myCommentsHasMore: true,
      myCommentsCurPage: 1,
      myLikes: [],
      myLikesLoading: false,
      myLikesLoaded: false,
      myLikesHasMore: true,
      myLikesCurPage: 1,
      myBookmarks: [],
      myBookmarksLoading: false,
      myBookmarksLoaded: false,
      myBookmarksHasMore: true,
      myBookmarksCurPage: 1,
      pageSize: 20,
      // Profile stats（发帖数/评论数/获赞数）
      statsPosts: 0,
      statsComments: 0,
      statsLikesReceived: 0,
      userCreatedAt: 0,
      // Profile search popup
      showProfileSearch: false,
      profileSearchTab: 0,
      profileSearchQuery: '',
      profileSearchResults: [],
      profileSearchLoading: false,
      profileSearchHasSearched: false,
      // 需求卡快捷设置（我的消息 tab 卡片右上角齿轮）
      showQuickSettings: false,
      quickSettingsSession: null
    }
  },
  computed: {
    isLoggedIn () {
      return this.$store.state.alreadyLogin
    },
    // 卡片右上角快捷设置动作项（与群详情设置菜单同款能力）
    quickSettingActions () {
      var s = this.quickSettingsSession
      if (!s) return []
      var actions = []
      if (s.public == 1) {
        actions.push({
          name: s.matchPaused == 1 ? this.$t('session_resumeMatching') : this.$t('session_pauseMatching'),
          value: 'toggleMatchPaused'
        })
      }
      actions.push({
        name: s.public == 1 ? this.$t('session_setPrivate') : this.$t('session_setPublic'),
        value: s.public == 1 ? 'setPrivate' : 'setPublic'
      })
      var directLabel = this.$t('aiChat_allowDirectContact')
      if (s.allowDirectContact == 1) {
        directLabel = directLabel + ' ✓'
      }
      actions.push({ name: directLabel, value: 'toggleDirectContact' })
      return actions
    },
    // 他人只读 profile：路由带 userId 参数时进入（/personal-center?userId=xxx）
    isOtherProfile () {
      return !!this.$route.query.userId
    },
    joinedText () {
      if (!this.userCreatedAt) return ''
      const d = new Date(Number(this.userCreatedAt))
      if (isNaN(d.getTime())) return ''
      const isCn = this.$config && this.$config.region === 'cn'
      if (isCn) {
        return d.getFullYear() + '年' + (d.getMonth() + 1) + '月'
      }
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return months[d.getMonth()] + ' ' + d.getFullYear()
    },
    tabs () {
      // 他人只读 profile 仅保留「消息」tab
      if (this.isOtherProfile) {
        return [{ label: this.$t('profile_tab_messages'), icon: 'chat-o' }]
      }
      return [
        { label: this.$t('profile_tab_messages'), icon: 'chat-o' },
        { label: this.$t('profile_tab_comments'), icon: 'comment-o' },
        { label: this.$t('profile_tab_likes'), icon: 'like-o' },
        { label: this.$t('profile_tab_bookmarks'), icon: 'star-o' }
      ]
    },
    profileSearchTabs () {
      return [
        { label: this.$t('profile_tab_messages'), icon: 'chat-o' },
        { label: this.$t('profile_tab_comments'), icon: 'comment-o' },
        { label: this.$t('profile_tab_likes'), icon: 'like-o' },
        { label: this.$t('profile_tab_bookmarks'), icon: 'star-o' }
      ]
    }
  },
  mounted () {
    this.loadUserInfo()
    this.loadMySessions()
    // 他人只读 profile 不展示评论/获赞 stats，无需拉取
    if (!this.isOtherProfile) {
      this.loadStatsComments()
    }
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
  },
  watch: {
    isLoggedIn (newVal) {
      if (newVal) {
        this.loadUserInfo()
      }
    },
    profileSearchTab () {
      this.profileSearchResults = []
      this.profileSearchHasSearched = false
      if (this.profileSearchQuery.trim()) {
        this.performProfileSearch(this.profileSearchQuery.trim())
      }
    }
  },
  methods: {
    handleLogin () {
      this.$store.state.needLogin = true
    },
    handleBack () {
      this.$router.back()
    },
    goToSettings () {
      this.$router.push({ name: 'Settings' })
    },
    async loadUserInfo () {
      try {
        // 他人只读 profile：按 targetUserId 拉对方公开信息（不展示手机号/邮箱等隐私）
        if (this.isOtherProfile) {
          this.otherUserId = String(this.$route.query.userId || '')
          const resp = await axios.post('/api/user/get_basic_user_info', { targetUserId: this.otherUserId })
          if (resp.data && resp.data.code == '0' && resp.data.data) {
            var other = resp.data.data
            this.userMobile = ''
            this.userCreatedAt = other.createdAt || 0
            this.contactInfo = {
              email: '',
              userName: other.userName || '',
              avatar: other.avatar || '',
              bio: other.bio || ''
            }
          }
          return
        }
        const resp = await axios.post('/api/user/get_user_by_user_id', {})
        console.log('resp:', resp.data)
        if (resp.data && resp.data.code == '0') {
          var user = resp.data.data
          // CN版本只展示mobile，global版本只展示email
          var isCn = this.$config && this.$config.region === 'cn'
          this.userMobile = isCn ? (user.mobile || '') : (user.email || '')
          this.contactInfo = {
            email: user.email || '',
            userName: user.userName || '',
            avatar: user.avatar || '',
            bio: user.bio || ''
          }
          this.userCreatedAt = user.createdAt || 0
        }
      } catch (error) {
        console.error('Failed to load user info:', error)
      }
    },
    startEditName () {
      this.originalName = this.contactInfo.userName
      this.isEditingName = true
    },
    cancelEditName () {
      this.contactInfo.userName = this.originalName
      this.isEditingName = false
    },
    async handleSaveName () {
      try {
        this.saving = true

        // 验证 userName 必填
        if (!this.contactInfo.userName || this.contactInfo.userName.trim() === '') {
          Toast(this.$t('toast_name_required'))
          return
        }

        const userId = this.$root.$common.getUserId()
        if (!userId) {
          Toast(this.$t('toast_not_logged_in'))
          return
        }

        const req = {
          userId: userId,
          userName: this.contactInfo.userName
        }

        const resp = await axios.post('/api/user/update_contact_info', req)
        if (resp.data && resp.data.code == '0') {
          Toast(this.$t('toast_save_success'))
          this.isEditingName = false
        } else {
          Toast(this.$t('toast_save_failed'))
        }
      } catch (error) {
        console.error('Failed to save name:', error)
        Toast(this.$t('toast_save_failed'))
      } finally {
        this.saving = false
      }
    },
    startEditBio () {
      this.originalBio = this.contactInfo.bio
      this.isEditingBio = true
    },
    cancelEditBio () {
      this.contactInfo.bio = this.originalBio
      this.isEditingBio = false
    },
    async handleSaveBio () {
      try {
        this.savingBio = true

        const userId = this.$root.$common.getUserId()
        if (!userId) {
          Toast(this.$t('toast_not_logged_in'))
          return
        }

        const req = {
          userId: userId,
          bio: this.contactInfo.bio || ''
        }

        const resp = await axios.post('/api/user/update_contact_info', req)
        if (resp.data && resp.data.code == '0') {
          Toast(this.$t('toast_save_success'))
          this.isEditingBio = false
        } else {
          Toast(this.$t('toast_save_failed'))
        }
      } catch (error) {
        console.error('Failed to save bio:', error)
        Toast(this.$t('toast_save_failed'))
      } finally {
        this.savingBio = false
      }
    },

    previewAvatar () {
      if (this.uploadingAvatar) return
      if (!this.contactInfo.avatar) {
        // 他人 profile 只读：无头像时不触发上传（避免误改观察者自己的资料）
        if (!this.isOtherProfile) {
          this.triggerAvatarUpload()
        }
        return
      }
      // 预览使用大图（l / base）
      ImagePreview({
        images: [this.$common.getFullImageUrl(this.contactInfo.avatar, this.$config, 'l')],
        closeable: true
      })
    },
    triggerAvatarUpload () {
      // 他人 profile 只读：任何入口都禁止上传
      if (this.isOtherProfile) return
      if (this.uploadingAvatar) return
      this.$refs.avatarInput.click()
    },
    handleAvatarChange (event) {
      const file = event.target.files[0]
      if (!file) return

      // 验证文件大小（最大 5MB）
      const maxSize = 5 * 1024 * 1024
      if (file.size > maxSize) {
        Toast(this.$t('toast_image_too_large'))
        event.target.value = ''
        return
      }

      // 保存文件并打开裁剪对话框
      this.selectedImageFile = file
      this.$nextTick(() => {
        this.$refs.cropDialog.show()
      })

      event.target.value = ''
    },
    async handleCropConfirm (croppedBlob) {
      if (!croppedBlob) return

      this.uploadingAvatar = true

      try {
        // 通过 multipart 上传到服务端，由服务端生成 s/m/l 三尺寸并上传 COS
        const formData = new FormData()
        formData.append('avatar', croppedBlob, `avatar_${Date.now()}.jpg`)

        const uploadResponse = await axios.post('/api/user/upload_avatar', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (!uploadResponse.data || uploadResponse.data.code !== 0) {
          Toast(this.$t('toast_upload_link_failed'))
          return
        }

        const imagePath = uploadResponse.data.data && uploadResponse.data.data.avatar
        if (!imagePath) {
          Toast(this.$t('toast_upload_link_failed'))
          return
        }

        // 更新数据库
        const userId = this.$root.$common.getUserId()
        if (!userId) {
          Toast(this.$t('toast_not_logged_in'))
          return
        }

        const updateResp = await axios.post('/api/user/update_contact_info', {
          userId: userId,
          avatar: imagePath
        })

        if (updateResp.data && updateResp.data.code == '0') {
          this.contactInfo.avatar = imagePath
          Toast(this.$t('toast_avatar_updated'))
        } else {
          Toast(this.$t('toast_avatar_failed'))
        }
      } catch (error) {
        console.error('Upload avatar error:', error)
        Toast(this.$t('toast_upload_failed'))
      } finally {
        this.uploadingAvatar = false
      }
    },
    switchTab (idx) {
      if (idx === this.selectedTab) return
      if (idx === 0 && !this.mySessionsLoading) {
        this.selectedTab = idx
        this.loadMySessions()
      } else if (idx === 1 && !this.myCommentsLoading) {
        this.selectedTab = idx
        this.loadMyComments()
      } else if (idx === 2 && !this.myLikesLoading) {
        this.selectedTab = idx
        this.loadMyLikes()
      } else if (idx === 3 && !this.myBookmarksLoading) {
        this.selectedTab = idx
        this.loadMyBookmarks()
      } else {
        this.selectedTab = idx
      }
    },
    async loadMySessions () {
      this.mySessionsLoading = true
      this.mySessionsLoaded = false
      this.mySessions = []
      this.mySessionsCurPage = 1
      this.mySessionsHasMore = true
      try {
        const resp = await axios.post('/api/chat/pull_chat_sessions', {
          pageSize: this.pageSize,
          curPage: 1,
          // 他人视角：拉对方的公开帖
          targetUserId: this.otherUserId || undefined
        })
        if (resp.data && resp.data.code == '0' && resp.data.data) {
          const sessions = (resp.data.data.chatSessions || []).map(s => this.normalizeSession(s))
          this.mySessions = sessions
          this.mySessionsCurPage = 2
          if (sessions.length < this.pageSize) {
            this.mySessionsHasMore = false
          }
          this.mySessionsLoaded = true
          // total/totalLikeCnt 为发帖数和获赞数（long 按 string 传，parseInt 兼容）
          this.statsPosts = parseInt(resp.data.data.total) || 0
          this.statsLikesReceived = parseInt(resp.data.data.totalLikeCnt) || 0
        }
      } catch (error) {
        console.error('Failed to load my sessions:', error)
      } finally {
        this.mySessionsLoading = false
      }
    },
    async loadMoreMySessions () {
      if (this.mySessionsLoading || !this.mySessionsHasMore) return
      this.mySessionsLoading = true
      try {
        const resp = await axios.post('/api/chat/pull_chat_sessions', {
          pageSize: this.pageSize,
          curPage: this.mySessionsCurPage,
          targetUserId: this.otherUserId || undefined
        })
        if (resp.data && resp.data.code == '0' && resp.data.data) {
          const sessions = (resp.data.data.chatSessions || []).map(s => this.normalizeSession(s))
          this.mySessions = this.mySessions.concat(sessions)
          this.mySessionsCurPage++
          if (sessions.length < this.pageSize) {
            this.mySessionsHasMore = false
          }
        }
      } catch (error) {
        console.error('Failed to load more sessions:', error)
      } finally {
        this.mySessionsLoading = false
      }
    },
    async loadMyComments () {
      this.myCommentsLoading = true
      this.myCommentsLoaded = false
      this.myComments = []
      this.myCommentsCurPage = 1
      this.myCommentsHasMore = true
      try {
        const resp = await axios.post('/api/chat_session_comment/list_mine', {
          pageSize: this.pageSize,
          curPage: 1
        })
        if (resp.data && resp.data.code == '0' && resp.data.data) {
          const items = resp.data.data.items || []
          this.myComments = items.map(item => ({
            comment: item.comment || {},
            chatSession: item.chatSession ? this.normalizeSession(item.chatSession) : null
          }))
          this.myCommentsCurPage = 2
          if (items.length < this.pageSize) {
            this.myCommentsHasMore = false
          }
          this.myCommentsLoaded = true
          this.statsComments = parseInt(resp.data.data.total) || 0
        }
      } catch (error) {
        console.error('Failed to load my comments:', error)
      } finally {
        this.myCommentsLoading = false
      }
    },
    async loadMoreMyComments () {
      if (this.myCommentsLoading || !this.myCommentsHasMore) return
      this.myCommentsLoading = true
      try {
        const resp = await axios.post('/api/chat_session_comment/list_mine', {
          pageSize: this.pageSize,
          curPage: this.myCommentsCurPage
        })
        if (resp.data && resp.data.code == '0' && resp.data.data) {
          const items = resp.data.data.items || []
          const newItems = items.map(item => ({
            comment: item.comment || {},
            chatSession: item.chatSession ? this.normalizeSession(item.chatSession) : null
          }))
          this.myComments = this.myComments.concat(newItems)
          this.myCommentsCurPage++
          if (items.length < this.pageSize) {
            this.myCommentsHasMore = false
          }
        }
      } catch (error) {
        console.error('Failed to load more comments:', error)
      } finally {
        this.myCommentsLoading = false
      }
    },
    async loadMyLikes () {
      this.myLikesLoading = true
      this.myLikesLoaded = false
      this.myLikes = []
      this.myLikesCurPage = 1
      this.myLikesHasMore = true
      try {
        const resp = await axios.post('/api/chat_session_like/list_mine', {
          pageSize: this.pageSize,
          curPage: 1
        })
        if (resp.data && resp.data.code == '0' && resp.data.data) {
          const sessions = (resp.data.data.chatSessions || []).map(s => this.normalizeSession(s))
          this.myLikes = sessions
          this.myLikesCurPage = 2
          if (sessions.length < this.pageSize) {
            this.myLikesHasMore = false
          }
          this.myLikesLoaded = true
        }
      } catch (error) {
        console.error('Failed to load my likes:', error)
      } finally {
        this.myLikesLoading = false
      }
    },
    async loadMoreMyLikes () {
      if (this.myLikesLoading || !this.myLikesHasMore) return
      this.myLikesLoading = true
      try {
        const resp = await axios.post('/api/chat_session_like/list_mine', {
          pageSize: this.pageSize,
          curPage: this.myLikesCurPage
        })
        if (resp.data && resp.data.code == '0' && resp.data.data) {
          const sessions = (resp.data.data.chatSessions || []).map(s => this.normalizeSession(s))
          this.myLikes = this.myLikes.concat(sessions)
          this.myLikesCurPage++
          if (sessions.length < this.pageSize) {
            this.myLikesHasMore = false
          }
        }
      } catch (error) {
        console.error('Failed to load more likes:', error)
      } finally {
        this.myLikesLoading = false
      }
    },
    async loadMyBookmarks () {
      this.myBookmarksLoading = true
      this.myBookmarksLoaded = false
      this.myBookmarks = []
      this.myBookmarksCurPage = 1
      this.myBookmarksHasMore = true
      try {
        const resp = await axios.post('/api/chat_session_bookmark/list_mine', {
          pageSize: this.pageSize,
          curPage: 1
        })
        if (resp.data && resp.data.code == '0' && resp.data.data) {
          const sessions = (resp.data.data.chatSessions || []).map(s => this.normalizeSession(s))
          this.myBookmarks = sessions
          this.myBookmarksCurPage = 2
          if (sessions.length < this.pageSize) {
            this.myBookmarksHasMore = false
          }
          this.myBookmarksLoaded = true
        }
      } catch (error) {
        console.error('Failed to load my bookmarks:', error)
      } finally {
        this.myBookmarksLoading = false
      }
    },
    async loadMoreMyBookmarks () {
      if (this.myBookmarksLoading || !this.myBookmarksHasMore) return
      this.myBookmarksLoading = true
      try {
        const resp = await axios.post('/api/chat_session_bookmark/list_mine', {
          pageSize: this.pageSize,
          curPage: this.myBookmarksCurPage
        })
        if (resp.data && resp.data.code == '0' && resp.data.data) {
          const sessions = (resp.data.data.chatSessions || []).map(s => this.normalizeSession(s))
          this.myBookmarks = this.myBookmarks.concat(sessions)
          this.myBookmarksCurPage++
          if (sessions.length < this.pageSize) {
            this.myBookmarksHasMore = false
          }
        }
      } catch (error) {
        console.error('Failed to load more bookmarks:', error)
      } finally {
        this.myBookmarksLoading = false
      }
    },
    async loadStatsComments () {
      // 轻量拉取评论总数（pageSize=1 只为 total），供头部 stats 展示
      try {
        const resp = await axios.post('/api/chat_session_comment/list_mine', {
          pageSize: 1,
          curPage: 1
        })
        if (resp.data && resp.data.code == '0' && resp.data.data) {
          this.statsComments = parseInt(resp.data.data.total) || 0
        }
      } catch (error) {
        console.error('Failed to load comments stats:', error)
      }
    },
    async navigateToSession (chatSessionId) {
      if (!chatSessionId) return
      try {
        const resp = await axios.post('/api/channel/get_session_detail', {
          chatSessionId: String(chatSessionId)
        })
        if (resp.data && resp.data.code == '0' && resp.data.data && resp.data.data.session) {
          const session = resp.data.data.session
          const channelId = session.channelId
          if (channelId) {
            this.$router.push({
              path: '/channel/' + channelId,
              query: { chatSessionId: String(chatSessionId) }
            })
          }
        }
      } catch (error) {
        console.error('Failed to navigate to session:', error)
      }
    },
    // Profile search popup methods
    openProfileSearch () {
      this.showProfileSearch = true
      this.profileSearchTab = this.selectedTab
      this.profileSearchQuery = ''
      this.profileSearchResults = []
      this.profileSearchHasSearched = false
    },
    clearProfileSearch () {
      this.profileSearchQuery = ''
      this.profileSearchResults = []
      this.profileSearchHasSearched = false
    },
    onProfileSearchInput () {
      var q = this.profileSearchQuery.trim()
      if (!q) {
        this.profileSearchResults = []
        this.profileSearchHasSearched = false
        return
      }
      clearTimeout(this._profileSearchTimer)
      this._profileSearchTimer = setTimeout(() => {
        this.performProfileSearch(q)
      }, 300)
    },
    async performProfileSearch (query) {
      this.profileSearchLoading = true
      this.profileSearchHasSearched = true
      var results = []
      try {
        if (this.profileSearchTab === 0) {
          // 我的消息：后端搜索我发布的 session
          const resp = await axios.post('/api/chat/search_sessions', {
            keyword: query,
            onlyMine: true,
            pageSize: 20,
            curPage: 1
          })
          if (resp.data && resp.data.data && resp.data.data.chatSessions) {
            results = resp.data.data.chatSessions.map(this.normalizeSession)
          }
        } else if (this.profileSearchTab === 1) {
          // 我的评论：后端按关键词过滤
          const resp = await axios.post('/api/chat_session_comment/list_mine', {
            keyword: query,
            pageSize: 20,
            curPage: 1
          })
          if (resp.data && resp.data.data && resp.data.data.items) {
            results = resp.data.data.items
          }
        } else if (this.profileSearchTab === 2) {
          // 我的赞：后端按关键词过滤
          const resp = await axios.post('/api/chat_session_like/list_mine', {
            keyword: query,
            pageSize: 20,
            curPage: 1
          })
          if (resp.data && resp.data.data && resp.data.data.chatSessions) {
            results = resp.data.data.chatSessions.map(this.normalizeSession)
          }
        } else if (this.profileSearchTab === 3) {
          // 我的收藏：后端按关键词过滤
          const resp = await axios.post('/api/chat_session_bookmark/list_mine', {
            keyword: query,
            pageSize: 20,
            curPage: 1
          })
          if (resp.data && resp.data.data && resp.data.data.chatSessions) {
            results = resp.data.data.chatSessions.map(this.normalizeSession)
          }
        }
      } catch (error) {
        console.error('[ProfileSearch] error:', error)
      } finally {
        this.profileSearchLoading = false
        this.profileSearchResults = results
      }
    },
    normalizeSession (s) {
      return {
        chatSessionId: String(s.chatSessionId || ''),
        channelId: String(s.channelId || ''),
        channelName: s.channelName || '',
        userMsg: s.userMsg || '',
        aiSummary: s.aiSummary || '',
        createdAt: Number(s.createdAt || 0),
        chatSessionCommentCnt: s.chatSessionCommentCnt || 0,
        likeCnt: s.likeCnt || 0,
        public: Number(s.public || 0),
        matchPaused: Number(s.matchPaused || 0),
        allowDirectContact: Number(s.allowDirectContact || 0),
        firstMediaContent: s.firstMediaContent || ''
      }
    },
    // 卡片右上角快捷设置
    openQuickSettings (session) {
      this.quickSettingsSession = session
      this.showQuickSettings = true
    },
    onQuickSettingSelect (action) {
      var s = this.quickSettingsSession
      this.showQuickSettings = false
      if (!s) return
      if (action.value === 'toggleMatchPaused') {
        this.quickToggleMatchPaused(s)
      } else if (action.value === 'setPrivate' || action.value === 'setPublic') {
        var publicVal = action.value === 'setPublic' ? 1 : 0
        var opts = publicVal == 1
          ? { title: this.$t('aiChat_setPublicTitle'), message: this.$t('aiChat_setPublicMessage') }
          : { message: this.$t('session_confirmSetPrivate') }
        Dialog.confirm(opts).then(() => {
          this.quickSetPublic(s, publicVal)
        }).catch(() => {})
      } else if (action.value === 'toggleDirectContact') {
        this.quickToggleDirectContact(s)
      }
    },
    async quickToggleMatchPaused (s) {
      try {
        var newMatchPaused = s.matchPaused == 1 ? 0 : 1
        const resp = await axios.post('/api/chat/update_match_paused', {
          chatSessionId: s.chatSessionId,
          matchPaused: newMatchPaused
        })
        if (resp.data.code === 0) {
          this.$set(s, 'matchPaused', newMatchPaused)
          Toast.success(newMatchPaused == 1 ? this.$t('session_matchPausedToast') : this.$t('session_matchResumedToast'))
        } else {
          Toast.fail(resp.data.data || 'Error')
        }
      } catch (e) {
        console.error('[PersonalCenter] quickToggleMatchPaused error:', e)
      }
    },
    async quickSetPublic (s, publicVal) {
      try {
        const resp = await axios.post('/api/chat/update_public', {
          chatSessionId: s.chatSessionId,
          public: publicVal == 1 ? 1 : -1
        })
        if (resp.data.code === 0) {
          this.$set(s, 'public', publicVal)
          Toast.success(publicVal == 1 ? this.$t('session_setPublic') : this.$t('session_setPrivate'))
        } else {
          Toast.fail(resp.data.data || 'Error')
        }
      } catch (e) {
        console.error('[PersonalCenter] quickSetPublic error:', e)
      }
    },
    async quickToggleDirectContact (s) {
      try {
        var newVal = s.allowDirectContact == 1 ? 0 : 1
        await axios.post('/api/channel/update_direct_contact', {
          chatSessionId: s.chatSessionId,
          allowDirectContact: newVal
        })
        this.$set(s, 'allowDirectContact', newVal)
        Toast.success(this.$t('session_setDirectContactSuccess'))
      } catch (e) {
        console.error('[PersonalCenter] quickToggleDirectContact error:', e)
      }
    },
    onScroll () {
      const scrollY = window.scrollY || document.documentElement.scrollTop
      const viewportHeight = window.innerHeight
      const fullHeight = document.documentElement.scrollHeight
      if (scrollY + viewportHeight < fullHeight - 200) return
      if (this.selectedTab === 0) {
        this.loadMoreMySessions()
      } else if (this.selectedTab === 1) {
        this.loadMoreMyComments()
      } else if (this.selectedTab === 2) {
        this.loadMoreMyLikes()
      } else if (this.selectedTab === 3) {
        this.loadMoreMyBookmarks()
      }
    },

  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.back-button {
  padding: 10px 20px;
}

.personal-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
}

.user-info {
  width: 100%;
  max-width: 500px;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.user-detail {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 20px;
}

.user-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.mobile-display {
  font-size: 1.2rem;
  font-weight: 600;
  color: #1A1A1A;
}

.name-section,
.bio-section {
  width: 100%;
  max-width: 240px;
}

.bio-display {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.9rem;
}

.bio-value {
  color: #1A1A1A;
  font-weight: 500;
  flex: 1;
  word-break: break-all;
}

.bio-placeholder {
  color: #8A8A8A;
  flex: 1;
  font-style: italic;
}

.bio-edit {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bio-edit-actions {
  display: flex;
  gap: 8px;
}

.bio-input {
  width: 100%;
}

.name-display {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.name-label {
  color: #8A8A8A;
}

.name-value {
  color: #1A1A1A;
  font-weight: 500;
  flex: 1;
}

.name-edit {
  display: flex;
  align-items: center;
  gap: 8px;
}

.name-input {
  flex: 1;
  min-width: 0;
}

.edit-icon {
  font-size: 16px;
  color: #E8543A;
  cursor: pointer;
  transition: color 0.2s;
}

.edit-icon:hover {
  color: #C93E1A;
}

.login-prompt {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
}

.login-prompt-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.login-prompt-slogan {
  font-size: 13px;
  color: #8A8A8A;
}

.contact-info {
  max-width: 400px;
  margin: 20px auto 0;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.contact-info h3 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1A1A1A;
  text-align: center;
}

.avatar-wrapper {
  cursor: pointer;
  margin-bottom: 10px;
}

.avatar-container {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 16%;
  overflow: hidden;
  background: #E8543A;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: bold;
  color: white;
}

.avatar-uploading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-edit-icon {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 24px;
  height: 24px;
  background: #E8543A;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  color: white;
}

.settings-trigger {
  cursor: pointer;
  padding: 6px 10px;
  color: #8A8A8A;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
}

.settings-trigger:hover {
  color: #1A1A1A;
}

.settings-label {
  font-size: 13px;
}

.profile-tabs-section {
  width: 100%;
  max-width: 500px;
  margin-top: 16px;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.profile-tab-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 10px 0;
}

.profile-tab-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 16px;
  background: #F7F5F2;
  color: #4A4A4A;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.profile-tab-pill.active {
  background: #E8543A;
  color: #fff;
  font-weight: 600;
}

.profile-tab-search {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding: 4px;
  color: #4A4A4A;
  cursor: pointer;
}

.profile-tab-content {
  padding-bottom: 20px;
}

.tab-panel {
  min-height: 200px;
}

.tab-loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.tab-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #8A8A8A;
  font-size: 14px;
}

.tab-empty p {
  margin-top: 12px;
}

.profile-session-card,
.profile-comment-card {
  background: #fff;
  padding: 12px;
  border-radius: 16px;
  margin-bottom: 8px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
  text-align: left;
}

/* 我的消息卡片：右上角快捷设置齿轮 */
.profile-session-card-mine {
  position: relative;
}
.profile-session-quick-settings {
  position: absolute;
  top: 12px;
  right: 12px;
  cursor: pointer;
  padding: 2px;
}
/* 齿轮占位：首行内容右侧留白，避免文字被齿轮压住 */
.profile-session-card-mine .profile-channel-tag {
  margin-right: 24px;
}
.profile-session-card-mine .profile-session-usermsg {
  padding-right: 20px;
}

.profile-channel-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 8px;
  background: #F7F5F2;
  color: #4A4A4A;
  font-size: 11px;
  margin-bottom: 8px;
}

.profile-session-usermsg {
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-session-ai {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  line-height: 1.4;
  margin-top: 4px;
}

.profile-session-ai.has-user-msg {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.profile-session-ai-tag {
  display: inline-block;
  padding: 0 5px;
  background: #FEF0ED;
  color: #8A8A8A;
  font-size: 10px;
  border-radius: 4px;
  font-weight: 500;
  flex-shrink: 0;
  line-height: 1.4;
}

.profile-session-ai-text {
  font-size: 11px;
  color: #8A8A8A;
  line-height: 1.4;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-session-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
}

.profile-session-stats {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-stat {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: #8A8A8A;
}

.profile-stat.liked {
  color: #E8543A;
}

.profile-stat.bookmarked {
  color: #F5C842;
}

.profile-session-time {
  font-size: 11px;
  color: #bbb;
}

.profile-comment-bubble {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  border-radius: 10px;
  background: #FEF0ED;
}

.profile-comment-avatar,
.profile-comment-avatar-placeholder {
  width: 28px;
  height: 28px;
  border-radius: 16%;
  flex-shrink: 0;
}

.profile-comment-avatar {
  object-fit: cover;
}

.profile-comment-avatar-placeholder {
  background: #E8543A;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.profile-comment-body {
  flex: 1;
  min-width: 0;
}

.profile-comment-reply {
  font-size: 11px;
  color: #8A8A8A;
  margin-bottom: 2px;
}

.profile-comment-text {
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.5;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-comment-preview {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding-left: 4px;
  font-size: 12px;
  color: #8A8A8A;
}

.profile-comment-preview span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-comment-time {
  font-size: 11px;
  color: #bbb;
  margin-top: 6px;
  padding-left: 4px;
}

.load-more-row {
  text-align: center;
  padding: 16px 0;
}

/* Header actions row */
.header-actions-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Joined date + stats row */
.joined-text {
  font-size: 12px;
  color: #8A8A8A;
}

.profile-stats-row {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
}

.profile-stats-item {
  display: inline-flex;
  align-items: baseline;
  cursor: pointer;
}

.profile-stats-num {
  font-size: 14px;
  font-weight: 600;
  color: #4A4A4A;
  margin-right: 3px;
}

.profile-stats-label {
  font-size: 12px;
  color: #8A8A8A;
}

.profile-stats-dot {
  color: #C5C2BC;
  font-size: 12px;
  margin: 0 10px;
}

.header-action-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* Profile search popup */
.profile-search-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #FAFAF8;
}

.profile-search-header {
  display: flex;
  align-items: center;
  padding: 16px;
  background: white;
  border-bottom: 1px solid #E0DDD8;
}

.profile-search-input-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  background: #F7F5F2;
  border-radius: 20px;
  padding: 0 12px;
}

.profile-search-tabs {
  display: flex;
  background: white;
  border-bottom: 1px solid #E0DDD8;
  padding: 0 16px;
}

.profile-search-tab {
  flex: 1;
  text-align: center;
  padding: 12px 4px;
  font-size: 14px;
  color: #8A8A8A;
  cursor: pointer;
  position: relative;
}

.profile-search-tab.active {
  color: #E8543A;
  font-weight: 600;
}

.profile-search-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  right: 20%;
  height: 2px;
  background: #E8543A;
  border-radius: 1px;
}

.profile-search-result-card {
  background: white;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 8px;
  cursor: pointer;
}

.profile-search-result-card:active {
  background: #F7F5F2;
}

.profile-channel-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #E8F7FB;
  color: #3DAECF;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  margin-bottom: 6px;
}

.profile-search-result-title {
  font-size: 14px;
  font-weight: 600;
  color: #1A1A1A;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.profile-search-result-summary {
  font-size: 13px;
  color: #4A4A4A;
  line-height: 1.4;
  margin-top: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.profile-search-comment-content {
  font-size: 14px;
  color: #1A1A1A;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.profile-search-comment-session {
  font-size: 12px;
  color: #8A8A8A;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #F7F5F2;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// src/components/PersonalCenter.vue